import { useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatErr } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import Nav from "@/components/Nav";
import Banner from "@/components/Banner";
import AddToCalendar from "@/components/AddToCalendar";
import { CalendarDays, Dumbbell, Users, Video } from "lucide-react";

const LIBRE_UNLIMITED = 999;  // reserved for future capacity display

function toISODate(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function pad(n) { return String(n).padStart(2, "0"); }
function isSaturday(d) { return d.getDay() === 6; }
function slotRange(s) {
  return `${s.hh}h${s.mm ? pad(s.mm) : ""} → ${s.end_hh}h${s.end_mm ? pad(s.end_mm) : ""}`;
}

export default function Booking({ hideNav = false }) {
  const { user, refresh } = useAuth();
  const nav = useNavigate();
  const [date, setDate] = useState(() => {
    // Start with today, no day skipping (Sunday allowed if slots exist)
    return new Date();
  });
  const [slots, setSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [booking, setBooking] = useState(null);
  const [limitInfo, setLimitInfo] = useState(null);
  const [upgrading, setUpgrading] = useState(false);
  const [userBookings, setUserBookings] = useState([]);
  const [cancelDialog, setCancelDialog] = useState(null); // { slot, bookingId }
  const [confirmationDialog, setConfirmationDialog] = useState(null); // { booking, slot }
  const [weekQuotas, setWeekQuotas] = useState(null); // Quotas for the displayed week

  const loadWeekQuotas = useCallback(async (targetDate) => {
    if (!user) return;
    try {
      const dateStr = toISODate(targetDate);
      const { data } = await api.get(`/quotas/${dateStr}`);
      setWeekQuotas(data);
    } catch (e) {
      console.error("Failed to load week quotas:", e);
    }
  }, [user]);

  const loadUserBookings = useCallback(async () => {
    if (!user) return;
    try {
      const { data } = await api.get("/bookings/me");
      setUserBookings(data || []);
    } catch (e) {
      console.error("Failed to load user bookings:", e);
    }
  }, [user]);

  const load = useCallback(async (d) => {
    setLoadingSlots(true);
    try {
      const { data } = await api.get(`/bookings/slots?date=${toISODate(d)}`);
      setSlots(data.slots || []);
      
      // Load quotas for the displayed week
      await loadWeekQuotas(d);
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail));
    } finally {
      setLoadingSlots(false);
    }
  }, [loadWeekQuotas]);

  useEffect(() => { 
    load(date); 
    loadUserBookings();
  }, [date, load, loadUserBookings]);

  const book = async (slot_start, extra = {}) => {
    setBooking(slot_start + JSON.stringify(extra));
    try {
      const res = await api.post("/bookings", { slot_start, ...extra });
      toast.success("Créneau réservé !");
      
      // Optimistically update local quota (decrement by 1)
      if (weekQuotas && weekQuotas.quotas) {
        const bookingType = res.data?.booking_type || extra.kind;
        setWeekQuotas(prev => {
          if (!prev || !prev.quotas) return prev;
          const updated = {
            ...prev,
            quotas: prev.quotas.map(q => {
              if (q.type === bookingType || (bookingType === 'groupe' && q.type === 'coaching')) {
                return {
                  ...q,
                  used: q.used + 1,
                  remaining: Math.max(0, q.remaining - 1)
                };
              }
              return q;
            })
          };
          return updated;
        });
      }
      
      // Refresh data including user quotas
      await Promise.all([
        load(date),
        loadUserBookings(),
        refresh() // Refresh user to update remaining_this_week quotas
      ]);
      
      // Show confirmation dialog with calendar options
      if (res.data && res.data.id) {
        // Find the slot info for display
        const slotInfo = slots.find(s => s.slot_start === slot_start);
        setConfirmationDialog({
          booking: {
            id: res.data.id,
            slot_start: slot_start,
            duration_min: slotInfo?.duration_min || 60,
            slot_family: extra.family || slotInfo?.family || ''
          },
          slot: slotInfo
        });
      } else {
        // Fallback if no booking ID returned
        setTimeout(() => nav("/mon-espace"), 800);
      }
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (detail && typeof detail === "object" && detail.code === "weekly_limit") {
        setLimitInfo(detail);
      } else {
        toast.error(formatErr(detail));
      }
    } finally {
      setBooking(null);
    }
  };

  const cancelBooking = async () => {
    if (!cancelDialog) return;
    try {
      await api.delete(`/bookings/${cancelDialog.bookingId}`);
      toast.success("Réservation annulée !");
      setCancelDialog(null);
      
      // Optimistically update local quota (increment by 1)
      if (weekQuotas && weekQuotas.quotas && cancelDialog.bookingType) {
        const bookingType = cancelDialog.bookingType;
        setWeekQuotas(prev => {
          if (!prev || !prev.quotas) return prev;
          const updated = {
            ...prev,
            quotas: prev.quotas.map(q => {
              if (q.type === bookingType || (bookingType === 'groupe' && q.type === 'coaching')) {
                return {
                  ...q,
                  used: Math.max(0, q.used - 1),
                  remaining: q.remaining + 1
                };
              }
              return q;
            })
          };
          return updated;
        });
      }
      
      // Refresh data including user quotas
      await Promise.all([
        load(date),
        loadUserBookings(),
        refresh() // Refresh user to update remaining_this_week quotas
      ]);
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || "Erreur lors de l'annulation");
    }
  };

  const getUserBookingForSlot = (slotStart, slotFamily) => {
    if (!user || !userBookings.length) return null;
    
    // Normalize dates by removing timezone info for comparison
    // Both slots and bookings now use Europe/Paris timezone format
    const normalizeDate = (dateStr) => dateStr ? dateStr.split('+')[0] : '';
    const normalizedSlotStart = normalizeDate(slotStart);
    
    return userBookings.find(b => 
      b.status === "confirmed" && 
      normalizeDate(b.slot_start) === normalizedSlotStart &&
      b.slot_family === slotFamily
    );
  };

  const upgrade = async () => {
    if (!limitInfo?.next_plan?.lookup_key) return;
    setUpgrading(true);
    try {
      const { data } = await api.post("/payments/checkout", {
        lookup_key: limitInfo.next_plan.lookup_key,
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || "Erreur paiement");
      setUpgrading(false);
    }
  };

  const hasTrial = !!user && user.trial_available && !user.trial_used;
  const hasActiveSubscriptions = !!user && user.subscriptions && user.subscriptions.length > 0;
  const canBook = !!user && (hasActiveSubscriptions || hasTrial);
  
  // Check if user has any coaching subscription
  const hasCoachingPlan = !!user && user.subscriptions?.some(sub => sub.type === "coaching");
  const isCoachingContext = !!user && (hasCoachingPlan || hasTrial);

  const isPublicVisitor = !user; // Non-logged users are public visitors
  const slotLabel = (hour) => (isCoachingContext ? `${hour}h15` : `${hour}h`);
  
  // Compute quotas display from weekQuotas (for the displayed week)
  const getQuotasDisplay = () => {
    if (!user || !weekQuotas || !weekQuotas.quotas) {
      return [];
    }
    
    const quotas = [];
    
    weekQuotas.quotas.forEach(q => {
      let label = "";
      let icon = null;
      let accessible = true;
      
      if (q.type === "coaching") {
        label = "Présentiel";
        icon = <Dumbbell className="h-4 w-4" />;
      } else if (q.type === "libre") {
        label = "Accès Libre";
        icon = <CalendarDays className="h-4 w-4" />;
      } else if (q.type === "distanciel") {
        label = "Distanciel";
        icon = <Video className="h-4 w-4" />;
        accessible = false; // No calendar access for distanciel
      } else if (q.type === "groupe") {
        label = "Groupe";
        icon = <Users className="h-4 w-4" />;
      }
      
      quotas.push({
        type: q.type,
        label,
        icon,
        accessible,
        total: q.total,
        used: q.used,
        remaining: q.remaining
      });
    });
    
    return quotas;
  };
  
  const quotasDisplay = getQuotasDisplay();
  
  // Compute booked days for calendar pastille (blue dot indicator)
  const bookedDays = useMemo(() => {
    if (!userBookings || userBookings.length === 0) return [];
    
    return userBookings
      .filter(b => b.status === "confirmed" && b.slot_start)
      .map(b => {
        // slot_start format: "2025-01-15T09:00:00"
        const [datePart] = b.slot_start.split("T");
        const [year, month, day] = datePart.split("-").map(Number);
        return new Date(year, month - 1, day); // JS months are 0-indexed
      });
  }, [userBookings]);

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#000000' }}>
      {!hideNav && <Banner />}
      {!hideNav && <Nav />}
      <div className="mx-auto max-w-6xl px-6 py-16 lg:px-12">
        <p className="mb-3 text-xs font-medium uppercase tracking-[0.32em] text-stone-400">Réservation</p>
        <h1 className="font-display mb-4 text-5xl font-light tracking-tight text-white">Choisissez votre créneau</h1>
        <p className="mb-8 max-w-2xl text-base font-light text-stone-300">
          Créneaux du lundi au vendredi (9h → 20h30) et samedi matin (9h → 11h, Coaching et Accès Libre disponibles).
        </p>
        
        {/* Quotas display - Multi-subscriptions support */}
        {user && quotasDisplay.length > 0 && (
          <div className="mb-8 rounded-2xl border border-stone-700 p-6" style={{ backgroundColor: '#0a0a0a' }}>
            <p className="mb-1 text-xs font-medium uppercase tracking-[0.24em] text-stone-400">Vos droits</p>
            {weekQuotas?.week_label && (
              <p className="mb-4 text-sm text-stone-500">{weekQuotas.week_label}</p>
            )}
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {quotasDisplay.map((quota, idx) => (
                <div 
                  key={idx}
                  className="flex items-start gap-3 rounded-xl border border-stone-800 bg-stone-950 p-4"
                >
                  <div className="mt-1 flex-none rounded-lg bg-stone-800 p-2 text-stone-300">
                    {quota.icon}
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-medium text-white">{quota.label}</p>
                    {quota.accessible ? (
                      <>
                        <p className="mt-1 text-2xl font-semibold text-stone-100">
                          {quota.remaining}
                          <span className="text-base font-normal text-stone-400">
                            /{quota.total}
                          </span>
                        </p>
                        <p className="mt-1 text-xs text-stone-500">
                          {quota.remaining === 0 
                            ? "Quota épuisé" 
                            : `${quota.remaining} séance${quota.remaining > 1 ? 's' : ''} restante${quota.remaining > 1 ? 's' : ''}`
                          }
                        </p>
                      </>
                    ) : (
                      <p className="mt-1 text-xs text-stone-400">
                        Forfait actif, sans accès calendrier
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {isSaturday(date) && (
          <div
            data-testid="saturday-banner"
            className="mb-8 flex items-start gap-4 rounded-2xl border border-[#3b82f6]/40 bg-white p-5"
          >
            <div className="mt-1 h-2 w-2 flex-none rounded-full bg-[#3b82f6]" />
            <div>
              <p className="text-xs font-medium uppercase tracking-[0.24em] text-[#8B7D6B]">Samedi matin</p>
              <p className="mt-2 text-sm text-stone-700">
                Le samedi matin offre deux options : <strong>Coaching de groupe (9h → 10h30, 3 places)</strong> et <strong>Accès libre (9h → 11h, 1 place)</strong>. Les deux décomptent de votre quota hebdomadaire.
              </p>
            </div>
          </div>
        )}

        {!canBook && user && (
          <div data-testid="no-access-banner" className="mb-8 rounded-2xl border border-amber-200 bg-amber-50 p-6">
            <p className="text-sm text-amber-900">
              {user.trial_used 
                ? "Votre séance d'essai a déjà été utilisée. " 
                : "Aucun accès disponible. "}
              <button onClick={() => nav("/#plans")} className="underline underline-offset-4">Choisissez un forfait</button> pour continuer.
            </p>
          </div>
        )}
        
        {/* Public visitor banner - consultation only */}
        {!user && (
          <div data-testid="public-visitor-banner" className="mb-8 rounded-2xl border border-blue-200 bg-blue-50 p-6">
            <p className="text-sm font-medium text-blue-900 mb-2">📅 Calendrier en consultation uniquement</p>
            <p className="text-sm text-blue-800 mb-3">
              Ce calendrier vous permet de voir les places disponibles en temps réel. Pour réserver un créneau, contactez Samuel directement :
            </p>
            <div className="flex flex-col gap-2 sm:flex-row sm:gap-4">
              <a href="tel:0668497750" className="text-sm font-semibold text-blue-900 hover:text-blue-700 transition-colors">
                ☎️ 06.68.49.77.50
              </a>
              <a href="mailto:samuel.b1992@gmail.com" className="text-sm font-semibold text-blue-900 hover:text-blue-700 transition-colors">
                ✉️ samuel.b1992@gmail.com
              </a>
            </div>
          </div>
        )}

        <div className="grid gap-10 lg:grid-cols-5">
          <div className="lg:col-span-2">
            <div className="rounded-2xl border border-stone-700 p-6" style={{ backgroundColor: '#0a0a0a' }}>
              <Calendar
                mode="single"
                selected={date}
                onSelect={(d) => d && setDate(d)}
                disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0))}
                className="rounded-md text-white"
                data-testid="booking-calendar"
                modifiers={{ booked: bookedDays }}
                modifiersClassNames={{ booked: "has-booking" }}
              />
            </div>
          </div>
          <div className="lg:col-span-3">
            <p className="mb-4 text-xs uppercase tracking-[0.24em] text-white">
              {date.toLocaleDateString("fr-FR", { weekday: "long", day: "2-digit", month: "long" })}
            </p>
            {loadingSlots ? (
              <p className="text-sm text-stone-500">Chargement des créneaux…</p>
            ) : slots.length === 0 ? (
              <p data-testid="no-slots" className="text-sm text-stone-500">Pas de créneaux ce jour.</p>
            ) : (
              <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                {slots.map((s) => {
                  const activeLoad = booking && booking.startsWith(s.slot_start);
                  const familyLabel =
                    s.family === "coaching" ? "Coaching · 1h" :
                    s.family === "libre" ? "Accès libre · 2h" :
                    "Créneau · " + s.duration_min + "min";
                  
                  // Check if user has booked this slot
                  const userBooking = getUserBookingForSlot(s.slot_start, s.family);
                  const isUserBooked = !!userBooking;
                  
                  // Calculate remaining places
                  const totalRemaining = Object.values(s.remaining || {}).reduce((sum, val) => sum + val, 0);
                  const anyRemaining = totalRemaining > 0;
                  
                  // Check if user has the right subscription type for this slot
                  // Note: We check if user HAS a subscription of the right type,
                  // but we don't check remaining_this_week here because that's for the CURRENT week
                  // The backend will verify the quota for the specific week when booking
                  let hasMatchingSubscription = false;
                  if (user && user.subscriptions) {
                    if (s.family === "coaching") {
                      hasMatchingSubscription = user.subscriptions.some(
                        sub => sub.type === "coaching" && sub.status === "active"
                      );
                    } else if (s.family === "libre") {
                      hasMatchingSubscription = user.subscriptions.some(
                        sub => sub.type === "libre" && sub.status === "active"
                      );
                    }
                  }
                  
                  // Calculate capacity info
                  let capacityText = "";
                  if (s.closed || !anyRemaining) {
                    capacityText = "Complet";
                  } else if (s.family === "coaching") {
                    const coachingRemaining = s.remaining?.coaching || 0;
                    capacityText = coachingRemaining === 1 
                      ? "1 place disponible" 
                      : `${coachingRemaining} places disponibles`;
                  } else if (s.family === "libre") {
                    const libreRemaining = s.remaining?.libre || 0;
                    capacityText = libreRemaining === 1 
                      ? "1 place disponible" 
                      : `${libreRemaining} places disponibles`;
                  }
                  
                  // Slot is disabled if:
                  // - Not logged in or no matching subscription (unless trial available for coaching)
                  // - No remaining places and not user's booking
                  // - Currently booking this slot
                  // - Slot is closed and not user's booking
                  // IMPORTANT: If user has booked this slot, it should NEVER be disabled (to allow cancellation)
                  const canBookThisSlot = (
                    isUserBooked || 
                    hasMatchingSubscription || 
                    (hasTrial && s.family === "coaching")
                  );
                  const disabled = isUserBooked 
                    ? false  // User's own booking is always clickable (to cancel)
                    : (!canBookThisSlot || (!anyRemaining && !isUserBooked) || activeLoad || (s.closed && !isUserBooked));
                  const isBlocked = s.is_blocked;
                  
                  // Color based on family
                  const borderColor = s.family === "coaching" ? "border-blue-500" : "border-stone-400";
                  
                  // Handle click: if user booked, open cancel dialog; otherwise book
                  const handleSlotClick = () => {
                    if (isUserBooked) {
                      setCancelDialog({ slot: s, bookingId: userBooking.id, bookingType: userBooking.booking_type });
                    } else {
                      book(s.slot_start, { family: s.family });
                    }
                  };
                  
                  return (
                    <button
                      key={`${s.slot_start}-${s.family}`}
                      data-testid={`slot-${s.family}-${s.hh}${s.mm ? '-' + s.mm : ''}`}
                      disabled={disabled}
                      onClick={handleSlotClick}
                      style={{ backgroundColor: '#000000' }}
                      className={`hover-lift rounded-xl border-2 p-5 text-left transition-all relative ${borderColor} ${
                        !disabled
                          ? "hover:shadow-lg hover:shadow-stone-900/50"
                          : "cursor-not-allowed opacity-50"
                      }`}
                    >
                      {/* Badge MA SÉANCE */}
                      {isUserBooked && (
                        <div 
                          data-testid="my-session-badge"
                          className="absolute top-3 right-3 z-10 bg-green-600 text-white px-2 py-1 rounded text-[10px] font-semibold uppercase tracking-wider"
                        >
                          MA SÉANCE
                        </div>
                      )}
                      
                      <div className="flex items-center justify-between">
                        <p className="font-display text-2xl text-white">{slotRange(s)}</p>
                        <span className="rounded-full bg-stone-800 px-2 py-0.5 text-[10px] uppercase tracking-[0.2em] text-stone-300">{familyLabel}</span>
                      </div>
                      {isBlocked && s.block_reason ? (
                        <p className="mt-2 text-xs font-medium text-red-400">
                          🔒 {s.block_reason}
                        </p>
                      ) : (
                        <p className="mt-2 text-sm font-medium text-stone-300">
                          {isUserBooked ? "Cliquez pour annuler" : capacityText}
                        </p>
                      )}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>

      <Dialog open={!!limitInfo} onOpenChange={(o) => !o && setLimitInfo(null)}>
        <DialogContent data-testid="weekly-limit-dialog" className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl font-light">Limite hebdomadaire atteinte</DialogTitle>
            <DialogDescription className="text-sm">
              {limitInfo?.message || "Vous avez utilisé toutes les séances incluses dans votre forfait cette semaine."}
            </DialogDescription>
          </DialogHeader>
          {limitInfo?.next_plan ? (
            <div className="mt-2 rounded-xl border border-stone-200 bg-stone-50 p-5">
              <p className="text-xs uppercase tracking-[0.24em] text-stone-500">Forfait recommandé</p>
              <div className="mt-2 flex items-baseline justify-between">
                <p className="font-display text-lg text-stone-900">{limitInfo.next_plan.name}</p>
                <p className="font-display text-2xl text-stone-900">{limitInfo.next_plan.price} <span className="text-sm">€/mois</span></p>
              </div>
              <p className="mt-3 text-xs text-stone-500">
                Passez à ce forfait pour débloquer une séance supplémentaire par semaine.
              </p>
            </div>
          ) : (
            <p className="text-sm text-stone-500">
              Vous êtes déjà sur le forfait maximal. Reprogrammez la semaine prochaine ou annulez une réservation existante.
            </p>
          )}
          <DialogFooter className="mt-2 gap-2 sm:gap-2">
            <Button
              data-testid="weekly-limit-close"
              variant="ghost"
              onClick={() => setLimitInfo(null)}
              className="rounded-full"
            >
              Plus tard
            </Button>
            {limitInfo?.next_plan && (
              <Button
                data-testid="weekly-limit-upgrade"
                onClick={upgrade}
                disabled={upgrading}
                className="rounded-full bg-stone-900 px-6 hover:bg-stone-800"
              >
                {upgrading ? "Redirection…" : "Passer au forfait supérieur"}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cancel Booking Dialog */}
      <Dialog open={!!cancelDialog} onOpenChange={(o) => !o && setCancelDialog(null)}>
        <DialogContent data-testid="cancel-booking-dialog" className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl font-light">Annuler ma séance</DialogTitle>
            <DialogDescription className="text-sm">
              Êtes-vous sûr de vouloir annuler cette réservation ?
            </DialogDescription>
          </DialogHeader>
          {cancelDialog && (
            <div className="mt-2 rounded-xl border border-stone-200 bg-stone-50 p-5">
              <p className="text-xs uppercase tracking-[0.24em] text-stone-500">Créneau concerné</p>
              <div className="mt-2">
                <p className="font-display text-lg text-stone-900">
                  {cancelDialog.slot && slotRange(cancelDialog.slot)}
                </p>
                <p className="text-sm text-stone-600 mt-1">
                  {date.toLocaleDateString("fr-FR", { weekday: "long", day: "2-digit", month: "long" })}
                </p>
              </div>
              <p className="mt-3 text-xs text-stone-500">
                Cette séance sera libérée et votre quota hebdomadaire sera restauré.
              </p>
            </div>
          )}
          <DialogFooter className="mt-2 gap-2 sm:gap-2">
            <Button
              data-testid="cancel-booking-close"
              variant="ghost"
              onClick={() => setCancelDialog(null)}
              className="rounded-full"
            >
              Garder ma séance
            </Button>
            <Button
              data-testid="cancel-booking-confirm"
              onClick={cancelBooking}
              className="rounded-full bg-red-600 px-6 hover:bg-red-700 text-white"
            >
              Annuler ma séance
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Booking Confirmation Dialog with Calendar Options */}
      <Dialog open={!!confirmationDialog} onOpenChange={(open) => !open && setConfirmationDialog(null)}>
        <DialogContent className="sm:max-w-[550px]">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl font-light">✅ Réservation confirmée !</DialogTitle>
            <DialogDescription className="text-sm">
              Votre créneau a été réservé avec succès.
            </DialogDescription>
          </DialogHeader>
          
          {confirmationDialog && (
            <div className="space-y-4 py-4">
              <div className="rounded-xl border border-stone-200 bg-stone-50 p-5">
                <p className="text-xs uppercase tracking-[0.24em] text-stone-500">Votre séance</p>
                <div className="mt-2">
                  <p className="font-display text-lg text-stone-900">
                    {confirmationDialog.slot && slotRange(confirmationDialog.slot)}
                  </p>
                  <p className="text-sm text-stone-600 mt-1">
                    {date.toLocaleDateString("fr-FR", { weekday: "long", day: "2-digit", month: "long", year: "numeric" })}
                  </p>
                  <p className="text-xs text-stone-500 mt-2">
                    📍 7161, lieu dit la tournerie, 49270 Saint Laurent des Autels
                  </p>
                </div>
              </div>

              <div className="space-y-3">
                <p className="text-sm font-medium text-stone-700">Ajouter à votre calendrier :</p>
                <AddToCalendar booking={confirmationDialog.booking} variant="outline" size="sm" />
              </div>

              <p className="text-xs text-stone-500 text-center">
                💡 Conseil : Ajoutez cet événement à votre calendrier pour ne pas oublier !
              </p>
            </div>
          )}
          
          <DialogFooter className="gap-2 sm:gap-2">
            <Button
              variant="ghost"
              onClick={() => setConfirmationDialog(null)}
              className="rounded-full"
            >
              Fermer
            </Button>
            <Button
              onClick={() => {
                setConfirmationDialog(null);
                nav("/mon-espace");
              }}
              className="rounded-full bg-stone-900 px-6 hover:bg-stone-800"
            >
              Voir mes réservations
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
